import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import type {
  ClaimContact,
  CompleteOnboarding,
  SendContactClaimOtp,
  UpdateMyProfile,
} from '@/modules/auth/use-cases'
import type {
  LoadCurrentDocument,
  RecordLegalAcceptance,
} from '@/modules/legal/use-cases'
import type { AuditMetadata } from '@/shared/infra/audit'
import { collectAuditMetadata } from '@/shared/infra/audit'
import { writeCachedVersions } from '@/modules/legal/infra/cache'
import type { LegalDocument } from '@/shared/domain'
import {
  AccountExistsError,
  ContactClaimRequiredError,
  DuplicateContactError,
  isOtpSendRefusal,
  sendRefusalMessage,
} from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'
import { useOtpSendCooldown, useSubmitGuard } from '@/shared/ui/hooks'
import { isValidEmail } from '@/modules/auth/ui/validation'
import type { HunterProfileData } from '../types'

export type ContactClaimState = {
  identifier: string
  identifierType: 'email' | 'phone'
  codeSent: boolean
  pendingInvite: boolean
  holderUnregistered: boolean
}

export type HunterProfileFieldErrors = {
  firstName: string
  lastName: string
  contactValue: string
  phone: string
  flywayRegion: string
  tosAccepted: string
  privacyAccepted: string
}

const EMPTY_FIELD_ERRORS: HunterProfileFieldErrors = {
  firstName: '',
  lastName: '',
  contactValue: '',
  phone: '',
  flywayRegion: '',
  tosAccepted: '',
  privacyAccepted: '',
}

export type UseHunterProfileStepProps = {
  initialValue: HunterProfileData
  lockedEmail?: string | null
  locked: boolean
  nextOnboardingStep: number
  onUpdateMyProfile: UpdateMyProfile
  onCompleteOnboarding: CompleteOnboarding
  onSendContactClaimOtp: SendContactClaimOtp
  onClaimContact: ClaimContact
  onRecordLegalAcceptance: RecordLegalAcceptance
  onLoadCurrentDocument: LoadCurrentDocument
  onUpload: (file: File) => Promise<string>
  onPersistData: (data: HunterProfileData) => void
  onSuccess: () => void
}

export type UseHunterProfileStepResult = {
  firstName: string
  lastName: string
  contactValue: string
  phoneE164: string
  flywayRegion: string
  profilePhotoUrl: string | null
  tosAccepted: boolean
  privacyAccepted: boolean
  isSubmitting: boolean
  isEmailLocked: boolean
  isLocked: boolean
  documentsReady: boolean
  fieldErrors: HunterProfileFieldErrors
  submitError: string
  setFirstName: (value: string) => void
  setLastName: (value: string) => void
  setContactValue: (value: string) => void
  setPhone: (e164: string, valid: boolean) => void
  setFlywayRegion: (value: string) => void
  setProfilePhotoUrl: (value: string | null) => void
  setTosAccepted: (value: boolean) => void
  setPrivacyAccepted: (value: boolean) => void
  handleUpload: (file: File) => Promise<string>
  handleSubmit: (event: FormEvent) => void
  contactClaim: ContactClaimState | null
  claimCode: string
  claimError: string
  isClaiming: boolean
  isSendingClaimCode: boolean
  claimResendSeconds: number
  setClaimCode: (value: string) => void
  handleSendClaimCode: () => void
  handleConfirmClaim: () => void
  handleCancelClaim: () => void
}

export function useHunterProfileStep({
  initialValue,
  lockedEmail,
  locked,
  nextOnboardingStep,
  onUpdateMyProfile,
  onCompleteOnboarding,
  onSendContactClaimOtp,
  onClaimContact,
  onRecordLegalAcceptance,
  onLoadCurrentDocument,
  onUpload,
  onPersistData,
  onSuccess,
}: UseHunterProfileStepProps): UseHunterProfileStepResult {
  const isEmailLocked = typeof lockedEmail === 'string' && lockedEmail !== ''
  const [firstName, setFirstName] = useState<string>(initialValue.firstName)
  const [lastName, setLastName] = useState<string>(initialValue.lastName)
  const [contactValue, setContactValue] = useState<string>(initialValue.contactValue)
  const [phoneE164, setPhoneE164] = useState<string>(initialValue.phoneE164 ?? '')
  const [phoneValid, setPhoneValid] = useState<boolean>(
    initialValue.phoneE164 !== null && initialValue.phoneE164 !== '',
  )
  const [flywayRegion, setFlywayRegion] = useState<string>(initialValue.flywayRegion)
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(
    initialValue.profilePhotoUrl === '' ? null : initialValue.profilePhotoUrl,
  )
  const [tosAccepted, setTosAccepted] = useState<boolean>(false)
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean>(false)
  const { isSubmitting, runGuarded: runSubmitGuard } = useSubmitGuard()
  const [tosDoc, setTosDoc] = useState<LegalDocument | null>(null)
  const [privacyDoc, setPrivacyDoc] = useState<LegalDocument | null>(null)
  const [documentsLoadFailed, setDocumentsLoadFailed] = useState<boolean>(false)
  const [fieldErrors, setFieldErrors] =
    useState<HunterProfileFieldErrors>(EMPTY_FIELD_ERRORS)
  const [submitError, setSubmitError] = useState<string>('')
  const [contactClaim, setContactClaim] = useState<ContactClaimState | null>(null)
  const [claimCode, setClaimCode] = useState<string>('')
  const [claimError, setClaimError] = useState<string>('')
  const { isSubmitting: isClaiming, runGuarded: runClaimGuard } = useSubmitGuard()
  const { isSubmitting: isSendingClaimCode, runGuarded: runClaimSendGuard } = useSubmitGuard()
  const {
    secondsLeft: claimResendSeconds,
    applyRefusal: applyClaimRefusal,
    recordSend: recordClaimSend,
  } = useOtpSendCooldown(
    contactClaim
      ? { channel: contactClaim.identifierType, identifier: contactClaim.identifier }
      : null,
  )
  const auditMetadataRef = useRef<AuditMetadata>({})

  useEffect(() => {
    let cancelled = false
    collectAuditMetadata()
      .then((metadata) => {
        if (cancelled) return
        auditMetadataRef.current = metadata
      })
      .catch(() => {
        if (cancelled) return
        auditMetadataRef.current = {}
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setDocumentsLoadFailed(false)
    Promise.all([
      onLoadCurrentDocument({ documentType: 'terms_of_service' }),
      onLoadCurrentDocument({ documentType: 'privacy_policy' }),
    ])
      .then(([tos, privacy]) => {
        if (cancelled) return
        setTosDoc(tos)
        setPrivacyDoc(privacy)
        if (!tos || !privacy) {
          setDocumentsLoadFailed(true)
        }
      })
      .catch(() => {
        if (cancelled) return
        setDocumentsLoadFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [onLoadCurrentDocument])

  const documentsReady = tosDoc !== null && privacyDoc !== null

  const effectiveContact = isEmailLocked ? (lockedEmail as string) : contactValue

  const handleUpload = useCallback(
    async (file: File) => {
      return await onUpload(file)
    },
    [onUpload],
  )

  const setPhone = useCallback((e164: string, valid: boolean) => {
    setPhoneE164(e164)
    setPhoneValid(valid)
  }, [])

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      if (locked) {
        onSuccess()
        return
      }
      const trimmedFirst = firstName.trim()
      const trimmedLast = lastName.trim()
      const trimmedContact = effectiveContact.trim()
      const errors: HunterProfileFieldErrors = { ...EMPTY_FIELD_ERRORS }
      if (trimmedFirst === '') errors.firstName = 'Enter your first name'
      if (trimmedLast === '') errors.lastName = 'Enter your last name'
      if (!isEmailLocked) {
        if (trimmedContact === '') {
          errors.contactValue = 'Enter your email'
        } else if (!isValidEmail(trimmedContact)) {
          errors.contactValue = 'Enter a valid email'
        }
      }
      if (phoneE164 === '' || !phoneValid) errors.phone = 'Enter a valid phone number'
      if (flywayRegion === '') errors.flywayRegion = 'Select a flyway region'
      if (!tosAccepted) errors.tosAccepted = 'You must accept the Terms of Service'
      if (!privacyAccepted) errors.privacyAccepted = 'You must accept the Privacy Policy'

      const hasError = Object.values(errors).some((message) => message !== '')
      setFieldErrors(errors)
      setSubmitError('')
      if (hasError) return

      if (!tosDoc || !privacyDoc) {
        setSubmitError('Unable to load legal documents. Please refresh and try again.')
        return
      }

      await runSubmitGuard(async () => {
        try {
          await onUpdateMyProfile({
            firstName: trimmedFirst,
            lastName: trimmedLast,
            displayName: `${trimmedFirst} ${trimmedLast}`,
            ...(isEmailLocked ? {} : { email: trimmedContact }),
            phone: phoneE164,
            flywayRegion,
            onboardingStep: nextOnboardingStep,
          })
          const metadata = auditMetadataRef.current
          await Promise.all([
            onRecordLegalAcceptance({
              documentId: tosDoc.documentId,
              acceptanceMethod: 'registration',
              userAgent: metadata.userAgent,
              ipAddress: metadata.ipAddress,
            }),
            onRecordLegalAcceptance({
              documentId: privacyDoc.documentId,
              acceptanceMethod: 'registration',
              userAgent: metadata.userAgent,
              ipAddress: metadata.ipAddress,
            }),
          ])
          writeCachedVersions({
            [tosDoc.documentType]: tosDoc.version,
            [privacyDoc.documentType]: privacyDoc.version,
          })
          await onCompleteOnboarding()
          onPersistData({
            firstName: trimmedFirst,
            lastName: trimmedLast,
            contactValue: isEmailLocked ? (lockedEmail as string) : trimmedContact,
            phoneE164,
            flywayRegion,
            profilePhotoUrl: profilePhotoUrl ?? '',
          })
          onSuccess()
        } catch (err) {
          if (err instanceof ContactClaimRequiredError) {
            setContactClaim({
              identifier: err.identifier,
              identifierType: err.identifierType,
              codeSent: false,
              pendingInvite: err.pendingInvite,
              holderUnregistered: err.holderUnregistered,
            })
            setSubmitError(err.message)
          } else if (err instanceof AccountExistsError) {
            setFieldErrors((prev) => ({
              ...prev,
              ...(err.identifierType === 'phone'
                ? { phone: err.message }
                : { contactValue: err.message }),
            }))
          } else if (err instanceof DuplicateContactError) {
            setSubmitError('The entered email or phone belongs to an existing account.')
          } else {
            setSubmitError('Something went wrong. Please try again.')
          }
        }
      })
    },
    [
      locked,
      firstName,
      lastName,
      effectiveContact,
      isEmailLocked,
      lockedEmail,
      phoneE164,
      phoneValid,
      flywayRegion,
      tosAccepted,
      privacyAccepted,
      profilePhotoUrl,
      nextOnboardingStep,
      onUpdateMyProfile,
      onCompleteOnboarding,
      onRecordLegalAcceptance,
      onPersistData,
      onSuccess,
      tosDoc,
      privacyDoc,
      runSubmitGuard,
    ],
  )

  const handleSubmitRef = useRef(handleSubmit)
  handleSubmitRef.current = handleSubmit

  const handleSendClaimCode = useCallback(async () => {
    if (!contactClaim || claimResendSeconds > 0) return
    setClaimError('')
    await runClaimSendGuard(async () => {
      try {
        await onSendContactClaimOtp({
          identifier: contactClaim.identifier,
          identifierType: contactClaim.identifierType,
        })
        setContactClaim((prev) => (prev ? { ...prev, codeSent: true } : prev))
        recordClaimSend()
      } catch (err) {
        applyClaimRefusal(err)
        if (isOtpSendRefusal(err)) {
          setContactClaim((prev) => (prev ? { ...prev, codeSent: true } : prev))
        }
        setClaimError(
          isOtpSendRefusal(err)
            ? sendRefusalMessage(err, false)
            : reportError(err, { fallbackMessage: 'Failed to send the code. Please try again.' }),
        )
      }
    })
  }, [
    applyClaimRefusal,
    claimResendSeconds,
    contactClaim,
    onSendContactClaimOtp,
    recordClaimSend,
    runClaimSendGuard,
  ])

  const handleConfirmClaim = useCallback(async () => {
    if (!contactClaim || claimCode.trim() === '') return
    setClaimError('')
    await runClaimGuard(async () => {
      try {
        await onClaimContact({
          identifier: contactClaim.identifier,
          identifierType: contactClaim.identifierType,
          code: claimCode.trim(),
        })
        setContactClaim(null)
        setClaimCode('')
        setSubmitError('')
        handleSubmitRef.current({ preventDefault: () => undefined } as FormEvent)
      } catch (err) {
        setClaimError(
          reportError(err, { fallbackMessage: 'Verification failed. Please try again.' }),
        )
      }
    })
  }, [contactClaim, claimCode, onClaimContact, runClaimGuard])

  const handleCancelClaim = useCallback(() => {
    setContactClaim(null)
    setClaimCode('')
    setClaimError('')
  }, [])

  const effectiveSubmitError = submitError === '' && documentsLoadFailed
    ? 'Unable to load legal documents. Please refresh and try again.'
    : submitError

  const visibleContactClaim = useMemo(
    () =>
      contactClaim
        ? { ...contactClaim, codeSent: contactClaim.codeSent || claimResendSeconds > 0 }
        : null,
    [contactClaim, claimResendSeconds],
  )

  return useMemo(
    () => ({
      firstName,
      lastName,
      contactValue: effectiveContact,
      phoneE164,
      flywayRegion,
      profilePhotoUrl,
      tosAccepted,
      privacyAccepted,
      isSubmitting,
      isEmailLocked,
      isLocked: locked,
      documentsReady,
      fieldErrors,
      submitError: locked ? '' : effectiveSubmitError,
      setFirstName,
      setLastName,
      setContactValue,
      setPhone,
      setFlywayRegion,
      setProfilePhotoUrl,
      setTosAccepted,
      setPrivacyAccepted,
      handleUpload,
      handleSubmit,
      contactClaim: visibleContactClaim,
      claimCode,
      claimError,
      isClaiming,
      isSendingClaimCode,
      claimResendSeconds,
      setClaimCode,
      handleSendClaimCode,
      handleConfirmClaim,
      handleCancelClaim,
    }),
    [
      firstName,
      lastName,
      effectiveContact,
      phoneE164,
      flywayRegion,
      profilePhotoUrl,
      tosAccepted,
      privacyAccepted,
      isSubmitting,
      isEmailLocked,
      locked,
      documentsReady,
      fieldErrors,
      effectiveSubmitError,
      handleUpload,
      handleSubmit,
      setPhone,
      visibleContactClaim,
      claimCode,
      claimError,
      isClaiming,
      isSendingClaimCode,
      claimResendSeconds,
      handleSendClaimCode,
      handleConfirmClaim,
      handleCancelClaim,
    ],
  )
}
